import React, { useMemo } from 'react';

import KebabMenu from 'components/uiLibrary/KebabMenu';
import { PrimaryButton } from 'components/uiLibrary/LinkButton';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityEditButton, { useEntityEditPermissions } from 'components/Globals/EntityEditButton';
import EntityShareAccess from 'components/Globals/EntityShareAccess';
import FAQ from 'components/Globals/FAQ';
import useTheme, { THEMES } from 'components/Globals/Layout/ThemeProvider';
import ShareEntity from 'components/Globals/ShareEntity';

import { ENTITY_MAIN_TABS } from 'constants/index';

import { useProfileAccesses } from 'utils/hooks/useAuthenticatedUser';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, TP } from 'operabase-router/constants';

import classes from './Actions.module.scss';

/* NOTE: This will be enabled after the new bookmarking/save feature is released */
// <div key="bookmark" className={classnames(classes.bookmark, { [classes.bookmark__dark]: isDarkTheme })}>
//   {!isLoggedIn && isMobile ? (
//     <>
//       <SpriteIcon icon="bookmark_add" size={16} />
//       <span className={classes.bookmarkLabel}>{t(`${TP}.m_SAVE`)}</span>
//     </>
//   ) : (
//     <SpriteIcon icon="bookmark_add" size={16} />
//   )}
// </div>,

const Actions = ({ entityType, entity, trackingData }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { theme } = useTheme();
  const { permissions } = usePageContext();
  const isLoggedIn = permissions?.isLoggedIn;
  const isDarkTheme = theme === THEMES.DARK;
  const { showEditBtn } = useEntityEditPermissions();
  const { isMobile } = useDeviceTypeLayouts();
  const { organizationPermissions } = permissions;
  const { navigate } = usePageContext();
  const { data: profileAccesses } = useProfileAccesses();
  const hasProfileAccess = profileAccesses?.data?.some(access => access?.profile?.id === entity?.id);

  const actionComponents = useMemo(() => {
    if (!organizationPermissions && entityType === ENTITY_TYPES.ORGANIZATION) {
      return [];
    }

    const visibilityBoostLinkProps = navigate.getLinkProps({
      entity,
      entityType,
      edit: true,
      onlyLinkProps: true,
      queryParams: { tab: ENTITY_MAIN_TABS.VISIBILITY_BOOST },
    });

    return [
      showEditBtn && entity?.id && (
        <EntityEditButton
          key="edit"
          className={classes.editAction}
          size="medium"
          shape="rounded"
          trackingData={trackingData}
        />
      ),
      ...(entityType === ENTITY_TYPES.PROFILE && entity?.id && hasProfileAccess
        ? [
            <PrimaryButton
              {...visibilityBoostLinkProps}
              onClick={() => navigate.to(visibilityBoostLinkProps)}
              isLink
              preventDefault
            >
              {t(`${TP}.FN_EDIT_FEAT_VISIBILITY_BOOST`)}
            </PrimaryButton>,
          ]
        : []),

      <ShareEntity
        key="share"
        entityType={entityType}
        entity={entity}
        trackingData={{
          ...trackingData,
          component: COMPONENTS.SHARE_CTA,
        }}
        renderIconButton={isLoggedIn || !isMobile}
      />,

      // TODO: FB-000000 Junaid removing this workflow for now until the flow is fixed
      // ...(entity?.id &&
      //   ? [
      //       <EntityShareAccess
      //         key="share-access"
      //         entityType={entityType}
      //         entity={entity}
      //         withoutText
      //         trackingData={{
      //           ...trackingData,
      //           component: COMPONENTS.SHARE_ACCESS_CTA,
      //         }}
      //       />,
      //     ]
      //   : []),

      entity?.id && (
        <FAQ
          key="faq"
          entityType={entityType}
          trackingData={{
            ...trackingData,
            component: COMPONENTS.FAQ_CTA,
          }}
          renderIconButton={isLoggedIn || !isMobile}
        />
      ),
    ].filter(Boolean);
  }, [
    organizationPermissions,
    entityType,
    navigate,
    entity,
    showEditBtn,
    trackingData,
    hasProfileAccess,
    t,
    isLoggedIn,
    isMobile,
  ]);

  if (!actionComponents.length) {
    return null;
  }

  if (isMobile && !isLoggedIn) {
    return <KebabMenu options={actionComponents} />;
  }

  return (
    <div className={classes.actions}>
      <div className={classes.actions_left}>{actionComponents}</div>
    </div>
  );
};

export default Actions;
